/**
 * Add tickets: the same right sheet the ticket opens in, with only what is
 * needed to pick. With nothing typed it shows the top of the Team's pick list
 * (`GET /api/desks/:id/pick-list`, the very read Bailey picks from, in her
 * order) under why these tickets: the backlog as written, whose tickets, and
 * what Bailey skips. Typing searches the tracker, and a pasted Jira or Linear
 * link or key resolves to its ticket (the search route binds a pasted link to
 * its key). Thinking out loud goes to the Ask dock instead of a ticket form.
 *
 * One primary: Add, which starts each picked ticket through the Team's own
 * `POST /api/desks/:id/tickets`. Each lands accepted with planning started.
 * A pick is held by `source:identifier`, so a new search never drops it.
 *
 * Opened from a pick link (`/waiting?add=1&q=COREX-123`), the search starts
 * on the key, and a reader on several Teams picks the Team at the top.
 */
import { useEffect, useState } from "react";
import type { JobAuthority, TeamPickedToday } from "@yagni/shared";
import { parseTrackerLink } from "@yagni/shared";
import { Btn, Chip, RecordHead, SheetFoot } from "../queue/index.js";
import { askDockStore } from "../shell/askDockStore.js";
import { captureUiError } from "../sentry.js";
import { NativeSelect } from "../ui/native-select.js";
import {
  PickListBasis,
  TicketLink,
  pickListEmptySentence,
  usePickList,
  type PickListState,
} from "../teams/PickList.js";
import {
  REPO_LABEL,
  RepoSelect,
  readRepoList,
  refusalCode,
  type TeamRepo,
} from "../work/repoPick.js";

export interface AddTicketsSheetProps {
  deskId: string;
  teamName: string;
  /** The Team's tracker source line, e.g. "Jira · COREX"; null when it reads none. */
  source: string | null;
  /** The tracker filter as written; null when none. */
  filter: string | null;
  /** `TeamPickedToday` from the roster; null/undefined prints no fact. */
  picked: TeamPickedToday | null | undefined;
  /**
   * The rung of the first Team's Plan line, so the footer says whether Reeve
   * asks a person before the build or approves the plan on its own. Undefined
   * when the caller does not read the Team's lines (the Waiting page).
   */
  planAuthority?: JobAuthority | null;
  /** What the search box opens with (`?q=`), e.g. one ticket's key; nothing is added until Add. */
  initialQuery?: string;
  /** After a successful add: the first created work item id (null if none was created but some were already tracked). */
  onAdded: (firstWorkItemId: string | null) => void;
  onClose: () => void;
  /**
   * The Teams the reader may add to. With more than one, the sheet opens a
   * Team select at its top, starting on `deskId`, and Add posts to the chosen
   * Team. With one or none, the sheet is `deskId`'s alone.
   */
  teams?: ReadonlyArray<AddTicketsTeam>;
}

/** A Team the Add tickets sheet can post to. */
export interface AddTicketsTeam {
  id: string;
  name: string;
  pickedToday?: TeamPickedToday | null;
}

/** One ticket, as the candidates and search routes answer it. */
export interface TicketCandidate {
  source: "jira" | "linear";
  identifier: string;
  title: string;
  /** The ticket in its tracker, when the route carries it. */
  url?: string | null;
  onTeam: { workItemId: string; phase: string; deskId: string; deskName: string } | null;
}

export const ADD_SEARCH_DEBOUNCE_MS = 250;
/** How much of the pick list the sheet shows before pointing at search. */
export const PICK_LIST_SHOWN = 8;

const keyOf = (row: Pick<TicketCandidate, "source" | "identifier">) =>
  `${row.source}:${row.identifier}`;

function readRows(raw: unknown): TicketCandidate[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    const row = (entry ?? {}) as Record<string, unknown>;
    if ((row.source !== "jira" && row.source !== "linear") || typeof row.identifier !== "string")
      return [];
    const onTeam =
      row.onTeam && typeof row.onTeam === "object"
        ? (row.onTeam as TicketCandidate["onTeam"])
        : null;
    return [
      {
        source: row.source,
        identifier: row.identifier,
        title: typeof row.title === "string" ? row.title : row.identifier,
        url: typeof row.url === "string" ? row.url : null,
        onTeam,
      },
    ];
  });
}

function pickedFact(picked: TeamPickedToday | null | undefined): string | null {
  if (!picked) return null;
  return picked.cap != null
    ? `${picked.count} of ${picked.cap} picked today`
    : `${picked.count} picked today`;
}

/** The sentence a refused or partial start answers with, keeping what did not start. */
function failureSentence(body: Record<string, unknown> | null, fallback: string): string {
  const message =
    typeof body?.message === "string" && body.message.trim() ? body.message.trim() : null;
  if (message) return message;
  const failed = Array.isArray(body?.failed)
    ? (body!.failed as Array<Record<string, unknown>>)
    : [];
  const reasons = failed.flatMap((entry) =>
    typeof entry.message === "string" && entry.message.trim()
      ? [`${String(entry.ticketKey ?? "A ticket")}: ${entry.message.trim()}`]
      : [],
  );
  return reasons.length > 0 ? reasons.join(" ") : fallback;
}

function Row({
  row,
  picked,
  onToggle,
}: {
  row: TicketCandidate;
  picked: boolean;
  onToggle: () => void;
}) {
  const inQueue = row.onTeam != null;
  return (
    <label className={"brow linked" + (picked ? " on" : "")} aria-disabled={inQueue || undefined}>
      <input
        type="checkbox"
        checked={picked}
        disabled={inQueue}
        onChange={onToggle}
        aria-label={`${row.identifier} ${row.title}`}
      />
      <span>
        <span className="k">{row.identifier}</span>
        <div className="t">{row.title}</div>
        {inQueue && <div className="why">in the queue</div>}
      </span>
      <TicketLink ticket={row} />
    </label>
  );
}

/** The pick list's rows, or the one line that says why there are none yet. */
function PickListRows({
  state,
  rows,
  sourceWord,
  isPicked,
  onToggle,
}: {
  state: PickListState;
  rows: TicketCandidate[];
  sourceWord: string;
  isPicked: (row: TicketCandidate) => boolean;
  onToggle: (row: TicketCandidate) => void;
}) {
  const list = state.list;
  if (list == null && state.failed) {
    return (
      <p className="body-p">
        Could not read what Bailey reads just now.{" "}
        <button type="button" className="ref" onClick={state.recheck}>
          Check again
        </button>
      </p>
    );
  }
  if (list == null) return <p className="body-p">Reading {sourceWord}.</p>;
  if (rows.length === 0) return <p className="body-p">{pickListEmptySentence(list)}</p>;
  return (
    <>
      {rows.map((row) => (
        <Row key={keyOf(row)} row={row} picked={isPicked(row)} onToggle={() => onToggle(row)} />
      ))}
    </>
  );
}

/** What the sheet says when a start answered without a sentence of its own. */
function fallbackSentence(code: string | null, created: number, already: number): string {
  if (code === "choose_repo")
    return "Choose the repository these tickets build in, then add them again.";
  if (code === "needs_repo")
    return "This Team has no repository to build in yet. Add one in its settings first.";
  if (created > 0) return `${created} added. The rest did not start.`;
  if (already > 0)
    return `${already === 1 ? "That ticket is" : "Those tickets are"} already on a Team.`;
  return "Those tickets did not start. Try again.";
}

/**
 * What happens after Plan, in the Team's own terms. The Plan line's rung is
 * known on the Team page; elsewhere the sentence stays true of either step.
 */
export function addFooterPlanLine(planAuthority: JobAuthority | null | undefined): string {
  if (planAuthority === "autonomous")
    return "Reeve drafts and approves the plan on its own, and the build follows.";
  if (planAuthority === "supervised")
    return "Reeve drafts the plan and asks you before anything is built.";
  return "Reeve drafts the plan; nothing is built until it is approved.";
}

export function AddTicketsSheet({
  deskId: firstDeskId,
  teamName: firstTeamName,
  source: firstSource,
  filter: firstFilter,
  picked: firstPicked,
  planAuthority,
  onAdded,
  onClose,
  initialQuery,
  teams,
}: AddTicketsSheetProps) {
  const choices = teams && teams.length > 1 ? teams : [];
  const choosing = choices.length > 0;
  const [chosenId, setChosenId] = useState(firstDeskId);
  const chosen = choices.find((team) => team.id === chosenId) ?? null;
  const deskId = chosen?.id ?? firstDeskId;
  const teamName = chosen?.name ?? firstTeamName;
  // The source, filter and picked facts the caller passed are the first
  // Team's; another Team's are its roster entry's, or nothing.
  const onFirst = deskId === firstDeskId;
  const source = onFirst ? firstSource : null;
  const filter = onFirst ? firstFilter : null;
  const pickedToday = onFirst ? firstPicked : chosen?.pickedToday;
  const base = `/api/desks/${encodeURIComponent(deskId)}`;
  const sourceWord = source ?? "your tracker";
  const [query, setQuery] = useState(initialQuery?.trim() ?? "");
  const [hits, setHits] = useState<TicketCandidate[] | null>(null);
  // The search that last answered, as `base|words`. Whether a search is under
  // way is derived from it, never set when the query changes: a setState in
  // the search effect runs inside each keystroke's commit and leaves a render
  // pending, so a link typed in one burst (a paste-as-typing tool, ~3
  // characters a millisecond) piled up 50 of them and React threw "Maximum
  // update depth exceeded" from the input's onChange (YAGNI-FRONTEND-43).
  const [answered, setAnswered] = useState<string | null>(null);
  const [picks, setPicks] = useState<Map<string, TicketCandidate>>(new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [repos, setRepos] = useState<TeamRepo[]>([]);
  const [repoId, setRepoId] = useState("");

  // The pick list follows the chosen Team; the repositories a start offered
  // were the previous Team's, so a switch forgets them.
  const pickList = usePickList(deskId);
  useEffect(() => {
    setRepos([]);
    setRepoId("");
  }, [deskId]);

  const words = query.trim();
  const searchKey = `${base}|${words}`;
  const searching = words !== "" && answered !== searchKey;

  // Only the debounced read sets state, never the effect's own run.
  useEffect(() => {
    if (!words) return;
    let cancelled = false;
    // A pasted link or key names one ticket wherever it stands, so the
    // search reads the whole tracker, not only what is unstarted.
    const pasted = parseTrackerLink(words) != null;
    const params = new URLSearchParams({
      q: words,
      assigned: "any",
      status: pasted ? "any" : "unstarted",
    });
    const timer = setTimeout(() => {
      fetch(`${base}/tracker-search?${params}`, { credentials: "include" })
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
        .then((body: { rows?: unknown }) => {
          if (!cancelled) setHits(readRows(body?.rows));
        })
        .catch(() => {
          if (cancelled) return;
          setHits([]);
          setError(`Could not search ${sourceWord}. Try again.`);
        })
        .finally(() => {
          if (!cancelled) setAnswered(searchKey);
        });
    }, ADD_SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [words, base, searchKey, sourceWord]);

  const toggle = (row: TicketCandidate) => {
    setPicks((prev) => {
      const next = new Map(prev);
      const key = keyOf(row);
      if (next.has(key)) next.delete(key);
      else next.set(key, row);
      return next;
    });
  };

  const add = async () => {
    if (picks.size === 0 || busy) return;
    setBusy(true);
    setError(null);
    const sent = [...picks.values()];
    try {
      const res = await fetch(`${base}/tickets`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tickets: sent.map((row) => ({
            source: row.source,
            identifier: row.identifier,
            ...(repoId ? { repoId } : {}),
          })),
        }),
      });
      const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
      const offered = readRepoList(body?.repos);
      if (offered.length > 0) setRepos(offered);
      const created = (
        Array.isArray(body?.created) ? (body!.created as Array<{ workItemId?: unknown }>) : []
      ).filter((entry): entry is { workItemId: string } => typeof entry?.workItemId === "string");
      const already = Array.isArray(body?.alreadyTracked) ? body!.alreadyTracked.length : 0;
      if (res.ok || created.length > 0) window.dispatchEvent(new Event("tickets.invalidate"));
      const failed = Array.isArray(body?.failed) ? body!.failed.length : 0;
      const waiting = Array.isArray(body?.needsRepo) ? body!.needsRepo.length : 0;
      if (res.status === 201 && failed === 0 && waiting === 0) {
        onAdded(created[0]?.workItemId ?? null);
        return;
      }
      // Keep only the picks that did not start, and say why.
      const kept = new Set<string>();
      for (const entry of [
        ...(Array.isArray(body?.failed) ? body!.failed : []),
        ...(Array.isArray(body?.needsRepo) ? body!.needsRepo : []),
      ]) {
        const fields = (entry ?? {}) as Record<string, unknown>;
        const id =
          [fields.identifier, fields.ticketKey].find(
            (value): value is string => typeof value === "string",
          ) ?? null;
        for (const row of sent) if (row.identifier === id) kept.add(keyOf(row));
      }
      if (created.length > 0 || already > 0) {
        setPicks((prev) => new Map([...prev].filter(([key]) => kept.has(key))));
      }
      const code = refusalCode(body as { error?: unknown; code?: unknown } | null);
      setError(failureSentence(body, fallbackSentence(code, created.length, already)));
    } catch (err) {
      captureUiError(err, { area: "Work", operation: "add_tickets_start", deskId });
      setError("Those tickets did not start. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const rows = hits ?? [];
  const count = picks.size;
  const fact = pickedFact(pickedToday);
  const list = pickList.list;
  const next: TicketCandidate[] = (list?.next ?? []).map((t) => ({
    source: t.source,
    identifier: t.identifier,
    title: t.title,
    url: t.url,
    onTeam: null,
  }));
  const shown = next.slice(0, PICK_LIST_SHOWN);
  const more =
    next.length > shown.length
      ? `${next.length - shown.length} more ${list?.picking === false ? "open" : "Bailey reads"}. Search above, or paste a link.`
      : "Not here? Search above, or paste a link.";

  return (
    <>
      <RecordHead
        recordKey="ADD TICKETS"
        noExpand
        onClose={onClose}
        title={`Add tickets to ${teamName}`}
        meta={
          <>
            {source && <Chip>{source}</Chip>}
            {filter && <Chip>{filter}</Chip>}
            {fact && (
              <span className="fact" style={{ marginLeft: "auto", fontSize: 11.5 }}>
                {fact}
              </span>
            )}
          </>
        }
      />
      <div className="sbody">
        {choosing && (
          <div className="blk">
            <div className="bh">
              <span className="label">Team</span>
            </div>
            <NativeSelect
              selectSize="sm"
              value={deskId}
              disabled={busy}
              aria-label="Team"
              data-testid="add-tickets-team"
              onChange={(event) => {
                setChosenId(event.target.value);
                setError(null);
                setAnswered(null);
              }}
            >
              {choices.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </NativeSelect>
          </div>
        )}
        <div className="bsearch">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
            <circle cx="7" cy="7" r="4.5" />
            <path d="M10.5 10.5L14 14" />
          </svg>
          <input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setError(null);
              // A new query is unanswered until its own read lands; cleared,
              // the sheet goes back to the pick list.
              setAnswered(null);
              if (!event.target.value.trim()) setHits(null);
            }}
            placeholder={`Search ${sourceWord} or paste a Jira or Linear link`}
            aria-label={`Search ${sourceWord}`}
            autoComplete="off"
          />
        </div>
        {words ? (
          <div className="blk">
            <div className="bh">
              <span className="label">Matches in {sourceWord}</span>
              <span className="fact">{searching ? "Searching" : rows.length}</span>
            </div>
            {rows.map((row) => (
              <Row
                key={keyOf(row)}
                row={row}
                picked={picks.has(keyOf(row))}
                onToggle={() => toggle(row)}
              />
            ))}
            {!searching && hits != null && rows.length === 0 && (
              <p className="body-p">
                Nothing matches. Paste a Jira or Linear link, or describe it to YAGNI and Bailey
                writes the ticket.
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="blk" data-testid="add-tickets-pick-list">
              <div className="bh">
                <span className="label bailey">
                  {list && !list.picking ? "Open to add" : "Bailey reads next"}
                </span>
                {list && <span className="fact">{next.length}</span>}
              </div>
              {list && <PickListBasis list={list} />}
              <PickListRows
                state={pickList}
                rows={shown}
                sourceWord={sourceWord}
                isPicked={(row) => picks.has(keyOf(row))}
                onToggle={toggle}
              />
            </div>
            <div
              className="dashed"
              style={{
                padding: "12px 14px",
                display: "flex",
                gap: 10,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span style={{ fontSize: 12.5, lineHeight: 1.45 }}>{more}</span>
              <Btn
                variant="quiet"
                size="sm"
                onClick={() => askDockStore.prefill("Write a ticket for ")}
              >
                Describe it to YAGNI
              </Btn>
            </div>
          </>
        )}
        {repos.length > 0 && (
          <RepoSelect
            repos={repos}
            value={repoId}
            onChange={setRepoId}
            disabled={busy}
            label={REPO_LABEL}
            testId="add-tickets-repo"
          />
        )}
        {error && (
          <p className="wk-gate-err" role="alert">
            {error}
          </p>
        )}
      </div>
      <SheetFoot
        why={
          <>
            Each one starts at <b>Plan</b>. {addFooterPlanLine(onFirst ? planAuthority : undefined)}
          </>
        }
        primary={{
          label: count > 0 ? `Add ${count}` : "Add",
          disabled: count === 0 || busy,
          onClick: () => void add(),
        }}
      />
    </>
  );
}
